import { useEffect, useState, type ReactNode } from 'react';
import { useTrack } from '@/analytics';
import { AppLogo } from '@/components/icons/AppLogo';
import { useAppAuth } from '@/lib/useAppAuth';
import { persistFtueInitiated } from '@/lib/ftue/api';
import { useFtueCompletion } from './ftueCompletion';

/**
 * Full-screen FTUE chrome, shared by every FTUE arm: a fixed overlay above
 * the whole app (the lobby loads behind it and is revealed the moment the
 * FTUE completes), a slim header with the brand mark and an always-available
 * **Skip** (skipping IS completing — the FTUE is one-shot either way), and
 * the **welcome screen** shown before the arm's content.
 *
 * the stack).
 */
/**
 * Opaque boot cover shown while the FTUE assignment is still resolving
 * (LaunchDarkly init) for a fresh post-signup player — the lobby must never
 * flash behind the FTUE. Control users see this for a sub-second beat and
 * then the lobby; FTUE users transition cover → welcome with no leak.
 */
export function FtueBootCover() {
  return (
    <div
      data-testid="ftue-boot-cover"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-[linear-gradient(to_bottom,var(--background),var(--background-end)_420px)]"
    >
      <div className="animate-pulse">
        <AppLogo width={190} />
      </div>
    </div>
  );
}

export function FtueShell({ variant, children }: { variant: string; children: ReactNode }) {
  const [welcomed, setWelcomed] = useState(false);
  const { user } = useAppAuth();
  const track = useTrack();
  const { markCompleted } = useFtueCompletion();

  // The FTUE is now on screen — stamp initiation on the backend (first write
  // wins). This is what lets a player who bails mid-FTUE finish it later,
  // even after the 12h freshness window closes.
  useEffect(() => {
    void persistFtueInitiated().catch((err) => {
      console.warn('[ftue] failed to persist ftue-initiated', err);
    });
  }, []);

  function skip() {
    track('ftue_completed', { skipped: true });
    markCompleted({ outcome: 'SKIPPED', variant });
  }

  const firstName = (user?.displayName ?? '').trim().split(/\s+/)[0] || null;

  return (
    <div
      data-testid="ftue-shell"
      // z-[60]: above the app chrome (SideNav z-40, BottomNav z-50, bottom
      // sheets z-50) — the FTUE fully owns the screen. Legal enforcement is
      // not weakened: a fresh signup has just accepted terms, and the server
      // ConsentGuard stays authoritative regardless of what's on top.
      className="fixed inset-0 z-[60] overflow-y-auto bg-[linear-gradient(to_bottom,var(--background),var(--background-end)_420px)]"
    >
      <header className="flex items-center justify-between px-4 py-3 md:px-8 md:py-5">
        <AppLogo width={150} />
        <button
          data-testid="ftue-skip"
          onClick={skip}
          className="bg-transparent border-none p-0 cursor-pointer text-[14px] text-ink-muted underline underline-offset-2 hover:text-ink"
        >
          Skip
        </button>
      </header>

      <main className="flex min-h-[calc(100svh-72px)] flex-col items-center justify-center px-3.5 pb-10 md:px-8">
        {welcomed ? (
          children
        ) : (
          <div data-testid="ftue-welcome" className="flex max-w-[560px] flex-col items-center gap-4 text-center">
            <h1
              className="m-0 text-[34px] lg:text-[44px] font-bold tracking-[-0.68px] text-ink"
              style={{ textWrap: 'balance' }}
            >
              {firstName ? `Welcome, ${firstName}!` : 'Welcome!'}
            </h1>
            <p className="m-0 max-w-[38ch] text-[16px] lg:text-[18px] text-ink-muted">
              You're in. Before you dive into the markets, learn the whole game in 30 seconds — your first call is on us.
            </p>
            <button
              data-testid="ftue-welcome-start"
              onClick={() => setWelcomed(true)}
              className="mt-2 rounded-pill bg-purple px-10 py-4 text-[18px] font-bold text-white border-none cursor-pointer transition-transform duration-150 hover:scale-[1.04] active:scale-95 shadow-md"
            >
              Let's go
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
